import React, { useContext, useEffect, useState, useCallback } from "react";
import { useAppStore } from "../../../../../context/store";
import { Dialog, Portal, Field, CloseButton, Button, Input, VStack, HStack, SimpleGrid, Spacer, Editable } from "@chakra-ui/react";
import { extractEntityPropertiesDeep } from "../../../../../utilies/Utility";
import { rebuildEntityPropertiesFromFlatList } from "../../../../../utilies/EditingGeojsonUtility";
import { LuPlus, LuX } from "react-icons/lu";
import { Tooltip } from "../../../../ui/tooltip";
import { useMapObjects } from "../../../../../context/MapObjectsContext";

/* Helper: make unique key based on a given list */
function makeUniqueKeyFromList(list, base = "Key", excludeUID = null) {
  const used = new Set(list.filter((it) => it.__uid !== excludeUID).map((it) => it.key));

  let k = base || "Key";
  let i = 1;
  while (used.has(k)) {
    k = `${base}_${i++}`;
  }
  return k;
}

function getDefaultValueForNewKey(key, currentValue) {
  const normalizedKey = String(key ?? "")
    .trim()
    .toLowerCase();

  const value = String(currentValue ?? "");
  const isEmpty = value === "";
  const isPreviousAutoDefault = value === "stop" || value === "R1-1";

  if (!isEmpty && !isPreviousAutoDefault) {
    return currentValue;
  }

  if (normalizedKey === "type") return "R1-1";
  if (normalizedKey === "text") return "stop";

  return isPreviousAutoDefault ? "" : (currentValue ?? "");
}

/* Row component with local state and commit-on-blur/submit */
const FeatureRow = React.memo(
  function FeatureRow({ uid, k, v, onRemove, onCommitValue, onCommitRename }) {
    const [localValue, setLocalValue] = useState(v ?? "");
    const [localKey, setLocalKey] = useState(k ?? "");

    useEffect(() => {
      setLocalValue(v ?? "");
    }, [v]);

    useEffect(() => {
      setLocalKey(k ?? "");
    }, [k]);

    const handleBlur = () => {
      const normalized = localValue ?? "";
      if (normalized !== (v ?? "")) {
        onCommitValue(uid, normalized);
      }
    };

    const handleKeyCommit = (value) => {
      const normalized = String(value ?? "").trim();
      const prevKey = String(k ?? "");
      if (normalized !== prevKey) {
        onCommitRename(uid, normalized);
      }
    };

    return (
      <Field.Root mb={3} gap={0}>
        <HStack gap={0} mb={0}>
          <Button bg="transparent" p={0} m={0} onClick={() => onRemove(uid)}>
            <Tooltip content="Remove This Key">
              <LuX color="red" />
            </Tooltip>
          </Button>

          <Field.Label>
            <Editable.Root
              value={localKey}
              activationMode="dblclick"
              // details = { value: string, ... }
              onValueChange={(details) => setLocalKey(details.value ?? "")}
              onValueCommit={(details) => handleKeyCommit(details.value)}
            >
              <Editable.Preview />
              <Editable.Input />
            </Editable.Root>
          </Field.Label>
        </HStack>

        <Input placeholder={localKey || "Key"} variant="subtle" value={localValue} onChange={(e) => setLocalValue(e.target.value)} onBlur={handleBlur} />
      </Field.Root>
    );
  },
  (prev, next) => prev.uid === next.uid && prev.k === next.k && prev.v === next.v,
);

FeatureRow.displayName = "FeatureRow";

const LayersEditFeatureDialog = () => {
  // state
  const editFeatureDialog = useAppStore((s) => s.editFeatureDialog);
  const featureList = useAppStore((s) => s.featureList);
  const featureCached = useAppStore((s) => s.featureCached);

  // refs / setters
  const setEditFeatureDialog = useAppStore.getState().setEditFeatureDialog;
  const setFeatureList = useAppStore.getState().setFeatureList;
  const viewerRef = useAppStore.getState().viewerRef;
  const setFeatureCached = useAppStore.getState().setFeatureCached;

  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();

  const [layerName, setLayerName] = useState("");
  const [removedUIDs, setRemovedUIDs] = useState(new Set());

  const buildUID = useCallback((item, idx) => {
    const path = item.path || item.fullPath || "";
    return `${path}::${item.key}::${idx}`;
  }, []);

  useEffect(() => {
    if (editFeatureDialog && editFeatureDialog.open) {
      const entity = editFeatureDialog.entity;
      const raw = extractEntityPropertiesDeep(entity) || [];

      const withUIDs = raw.map((it, idx) => {
        const key = String(it.key ?? "");

        return {
          key,
          value: getDefaultValueForNewKey(key, it.value),
          path: it.path || "",
          fullPath: it.fullPath || "",
          __uid: buildUID(it, idx),
        };
      });

      // autoAdd: add missing keys as empty values when dialog opens
      const autoAdd = Array.isArray(editFeatureDialog.autoAdd) ? editFeatureDialog.autoAdd : null;
      if (autoAdd && autoAdd.length > 0) {
        const existingKeys = new Set(withUIDs.map((x) => String(x.key ?? "")));
        for (const s of autoAdd) {
          const k = String(s ?? "").trim();
          if (!k) continue;
          if (existingKeys.has(k)) continue;

          withUIDs.push({
            key: k,
            value: getDefaultValueForNewKey(k, ""),
            path: "",
            fullPath: "",
            __uid: `auto::${k}::${Date.now()}::${Math.random().toString(36).slice(2)}`,
          });
          existingKeys.add(k);
        }
      }

      setFeatureList(withUIDs);
      setRemovedUIDs(new Set());

      const dataSource = entity.entityCollection?.owner;
      setLayerName(dataSource?.layerName || "Unknown Layer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFeatureDialog]);

  const handleRemoveTemp = useCallback((uid) => {
    setRemovedUIDs((prev) => {
      const next = new Set(prev);
      next.add(uid);
      return next;
    });
  }, []);

  const handleCommitValue = useCallback(
    (uid, newValue) => {
      setFeatureList((prev) => {
        let changed = false;
        const out = prev.map((it) => {
          if (it.__uid === uid) {
            if (it.value !== newValue) {
              changed = true;
              return { ...it, value: newValue };
            }
            return it;
          }
          return it;
        });
        return changed ? out : prev;
      });
    },
    [setFeatureList],
  );

  const handleCommitRename = useCallback(
    (uid, rawNewKey) => {
      const trimmed = String(rawNewKey ?? "").trim();
      const base = trimmed.length ? trimmed : "Key";

      setFeatureList((prev) => {
        const unique = makeUniqueKeyFromList(prev, base, uid);
        let changed = false;

        const out = prev.map((it) => {
          if (it.__uid === uid) {
            if (it.key !== unique) {
              changed = true;
              return {
                ...it,
                key: unique,
                value: getDefaultValueForNewKey(unique, it.value),
              };
            }
            return it;
          }
          return it;
        });

        return changed ? out : prev;
      });
    },
    [setFeatureList],
  );

  const handleAddNew = useCallback(() => {
    setFeatureList((prev) => {
      const newKey = makeUniqueKeyFromList(prev, "Key");
      const newItem = {
        key: newKey,
        value: "",
        path: "",
        __uid: `::${newKey}::${Date.now()}::${Math.random().toString(36).slice(2)}`,
      };
      return [...prev, newItem];
    });
  }, [setFeatureList]);

  const updateFeature = useCallback(() => {
    setEditFeatureDialog((currentDialog) => {
      const entity = currentDialog?.entity;
      if (!entity) return currentDialog;

      const remaining = featureList.filter((it) => !removedUIDs.has(it.__uid));

      rebuildEntityPropertiesFromFlatList(entity, remaining, true);

      const temp = Array.isArray(mapObjectsRef.current) ? [...mapObjectsRef.current] : [];
      for (const obj of temp) {
        if (obj?.dataSource?.entities?.contains?.(entity)) {
          obj.is_edited = true;
          break;
        }
      }
      setMapObjects(temp);

      if (!currentDialog) {
        return currentDialog;
      }

      return {
        ...currentDialog,
        open: false,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureList, removedUIDs]);

  const displayedItems = featureList.filter((item) => !removedUIDs.has(item.__uid));

  return (
    <Dialog.Root
      lazyMount
      closeOnInteractOutside={false}
      open={!!(editFeatureDialog && editFeatureDialog.open)}
      onOpenChange={(e) => {
        setEditFeatureDialog((prev) => (prev ? { ...prev, open: e.open } : prev));
      }}
      autoFocus={false}
      initialFocus={null}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <VStack gap={0}>
                <Dialog.Title textAlign="left" width="full">
                  Edit Feature
                </Dialog.Title>
                <Dialog.Title width="full" fontSize="sm">
                  Layer: {layerName}
                </Dialog.Title>
              </VStack>
            </Dialog.Header>

            <Dialog.Body>
              <SimpleGrid columns={2} gap={2}>
                {displayedItems.map((it) => (
                  <FeatureRow key={it.__uid} uid={it.__uid} k={it.key} v={it.value} onRemove={handleRemoveTemp} onCommitValue={handleCommitValue} onCommitRename={handleCommitRename} />
                ))}
              </SimpleGrid>

              {removedUIDs.size > 0 && (
                <HStack mt={2}>
                  <span style={{ fontSize: 12 }}>{removedUIDs.size} key(s) marked for removal — will be deleted on Update.</span>
                </HStack>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <HStack w="full" align="center">
                <Button onClick={handleAddNew}>
                  Add New <LuPlus />
                </Button>
                <Spacer />
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Button onClick={updateFeature}>Update</Button>
              </HStack>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersEditFeatureDialog;
